import React, { forwardRef } from 'react';

import _get from 'lodash.get';
import moment from 'moment';

import { FilterFieldAPI, FilterFieldType } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isFunction, isObject, mobile, propagateProps } from '@philips-emr/tws-utils';

import { Popover } from '..';

import './filter-field.scss';

const { isMobile } = mobile;

const { DATE, DEFAULT, LIST, MONTH, OBJECT } = FilterFieldType;

const FilterFieldLabel = forwardRef(({ value, onClick }, ref) => { //eslint-disable-line
  const label = msg(value);

  return (
    <label className="c-filter-field__label" onClick={onClick} ref={ref}>{label}</label>
  );
});

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_ENTRY,
];

const MAX_HEIGHT = 700;

@AwesomeComponent({ type: ComponentType.FORM, api: FilterFieldAPI })
class FilterField extends React.Component {

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: object.notEnumerable(),
    entry: string,
    hideLabel: bool,
    inline: bool,
    label: expression,
    labelEntry: string,
    nativeOnMobile: bool,
    onBeforeClose: eventListener,
    onChange: eventListener,
    onClose: eventListener,
    onOpen: eventListener,
    type: oneOf(enumValues(FilterFieldType)),
    uuid: string,
  };

  static defaultProps = {
    data: {},
    label: 'Sample label',
    labelEntry: 'label',
    nativeOnMobile: true,
    type: DEFAULT,
  };

  state = {
    innerValue: this.getValue(),
    open: false,
  };

  componentDidUpdate(oldProps) {
    const { data, entry } = this.props;
    const { data: oldData } = oldProps;

    if (data[entry] !== oldData[entry]) {
      this.setState({
        innerOldValue: undefined,
        innerValue: data[entry],
      });
    }
  }

  shouldClose = () => {
    if (!this.state.open) {
      return false;
    }

    let close = true;

    const abortFn = () => close = false;
    const { onBeforeClose } = this.props;

    onBeforeClose({ abort: abortFn });

    return close;
  };

  getLabel() {
    if (this.getValue()) {
      const prettyValue = this.getPrettyValue();

      return prettyValue || this.props.label;
    }

    return this.props.label;
  }

  getPrettyValue() {
    const { type } = this.props;

    switch (type) {
      case LIST:
        return this.getPrettyList();
      case DATE:
        return this.getPrettyDate();
      case MONTH:
        return this.getPrettyMonth();
      case OBJECT:
        return this.getPrettyObject();
      default: {
        return this.getStringValue();
      }
    }
  }

  getPrettyList() {
    const value = this.getValue();

    if (!Array.isArray(value) || !value.length) {
      return this.props.label;
    }

    return value.length > 1 ? `${value[0].label}, +` : value[0].label;
  }

  getPrettyDate() {
    const value = this.getValue();

    if (!value || (Array.isArray(value) && !value.length)) {
      return this.props.label;
    }

    if (Array.isArray(value)) {
      const [startDate, endDate] = value;

      const startDateFormatted = startDate && moment.utc(startDate).format('DD MMM YYYY');
      const endDateFormatted = endDate && moment.utc(endDate).format('DD MMM YYYY');

      return `${startDateFormatted}${endDate ? ` - ${endDateFormatted}` : ''}`;
    }

    return moment.utc(value).format('DD MMM YYYY');
  }

  getPrettyMonth() {
    const value = this.getValue();

    return moment.utc(value).format('MMMM YYYY');
  }

  getPrettyObject() {
    const { labelEntry } = this.props;

    const value = this.getValue();

    return isObject(value) ? _get(value, labelEntry, '') : '';
  }

  getValue() {
    const { data, entry } = this.props;
    return _get(data, entry);
  }

  getStringValue() {
    return String(this.getValue());
  }

  onBlur = () => {
    const { entry, inline } = this.props;

    if (entry && inline) {
      this.triggerOnChange();
    }
  };

  onChange = ({ value, oldValue }) => {
    if (!this.props.entry) return;

    this.setState({
      innerOldValue: oldValue,
      innerValue: value,
    }, () => {
      isMobile() && this.triggerOnChange();
    });
  };

  triggerOnChange = () => {
    const { onChange, entry } = this.props;
    const { innerOldValue: oldValue, innerValue: value } = this.state;

    isFunction(onChange) && onChange({ entry, value, oldValue });
  };

  onClick = () => {
    const { onOpen } = this.props;

    this.setState({ open: true }, () => {
      isFunction(onOpen) && onOpen();
    });
  };

  onClose = () => {
    const shouldClose = this.shouldClose();

    if (!shouldClose) {
      return;
    }

    const { onClose } = this.props;

    this.triggerOnChange();

    this.setState({ open: false }, () => {
      isFunction(onClose) && onClose();
    });
  };

  getClassName() {
    const { inline, nativeOnMobile } = this.props;
    const { open } = this.state;

    return classNames({
      'c-filter-field': true,
      'is-active': open,
      'is-inline': inline || (isMobile() && nativeOnMobile),
    });
  }

  renderChildren() {
    const { children, nativeOnMobile } = this.props;
    const { innerValue } = this.state;

    return propagateProps(children, {
      nativeOnMobile,
      onAfterFetch: this.onAfterFetch,
      onBlur: this.onBlur,
      onChange: this.onChange,
      value: innerValue,
    });
  }

  renderContent() {
    const { uuid } = this.props;
    const Container = ContainerProvider.provide(this);

    if (isMobile()) {
      return this.renderChildren();
    }

    return (
      <Container acceptedTypes={acceptedTypes} className="c-filter-field__content" uuid={uuid}>
        {this.renderChildren()}
      </Container>
    );
  }

  renderInline() {
    const { hideLabel } = this.props;

    if (hideLabel) {
      return this.renderContent();
    }

    return (
      <>
        <FilterFieldLabel value={this.getLabel()} />
        {this.renderContent()}
      </>
    );
  }

  renderPopover() {
    const { open } = this.state;

    return (
      <Popover open={open} onClose={this.onClose} maxHeight={MAX_HEIGHT}>
        <FilterFieldLabel value={this.getLabel()} onClick={this.onClick}/>

        <Popover.Content>
          {this.renderContent()}
        </Popover.Content>
      </Popover>
    );
  }

  render() {
    const { inline, nativeOnMobile } = this.props;

    return (
      <div className={this.getClassName()}>
        {
          inline || (isMobile() && nativeOnMobile)
            ? this.renderInline()
            : this.renderPopover()
        }
      </div>
    );
  }
}

export default FilterField;
